import { createLabelWithDateRange, createOption, COMPARE_DATE_RANGE_OPTIONS, DATE_RANGE_OPTIONS, getStartOfDayHandleDST } from 'components/Common/DateRangePicker';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { addMinutes } from 'date-fns/esm';
import { DASHBOARD_API_CALL_TYPE, LEAD_TYPE_IDS, NAME_OF_LEAD_TYPES, NAME_OF_PRODUCT_TYPES, REPORTING_TIMEZONE } from './sf_constants';
import { getZonedEndOfDay, getZonedEndOfHour, getZonedEndOfQuarter, getZonedStartOfDay, getZonedStartOfQuarter, getZonedStartOfYear } from './time_utils';
import _ from 'lodash';

import {
  addDays, differenceInDays, format, addWeeks, subQuarters,
} from 'date-fns';
import {
  percIncrease, capitalizeWord,
} from './sf_utils';

/**
* @brief Function to calculate the metric diff in percent
* @param data{Object} Data metrics containing the date range
* @param from_tag{string} From tag in the meta tag
* @param to_tag{string} To tag in the meta tag
*/
export const calculateMetricDiffFromMetaTags = (data, from_tag, to_tag) => {
  const from_data = data.find((e) => (e.meta === from_tag));
  const to_data = data.find((e) => (e.meta === to_tag));

  return {

    data: {
      click_analytic_total_qf_leads:
        percIncrease(
          from_data?.click_analytic_total_qf_leads,
          to_data?.click_analytic_total_qf_leads,
        ),

      click_analytic_total_qf_calls:
        percIncrease(
          from_data?.click_analytic_total_qf_calls,
          to_data?.click_analytic_total_qf_calls,
        ),

      click_analytic_impressions:
        percIncrease(
          from_data?.click_analytic_impressions,
          to_data?.click_analytic_impressions,
        ),

      click_analytic_total_payout:
        percIncrease(
          from_data?.click_analytic_total_payout,
          to_data?.click_analytic_total_payout,
        ),

      click_analytic_clicks:
        percIncrease(
          from_data?.click_analytic_clicks,
          to_data?.click_analytic_clicks,
        ),

      click_analytic_est_total_payout:
        percIncrease(
          from_data?.click_analytic_est_total_payout,
          to_data?.click_analytic_est_total_payout,
        ),

      click_analytic_ctr:
        percIncrease(
          from_data?.click_analytic_ctr,
          to_data?.click_analytic_ctr,
        ),

      click_analytic_cpc:
        percIncrease(
          from_data?.click_analytic_cpc,
          to_data?.click_analytic_cpc,
        ),

      click_analytic_mobile_traffic:
        percIncrease(
          getMobileTrafficData(data, true),
          getMobileTrafficData(data, false),
        ),

      click_analytic_avg_position:
        percIncrease(
          from_data?.click_analytic_avg_position,
          to_data?.click_analytic_avg_position,
        ),

      click_analytic_avg_bid:
        percIncrease(
          from_data?.click_analytic_avg_bid,
          to_data?.click_analytic_avg_bid,
        ),

    },
  };
};

export const getImpressionsForGraph = (props) => props?.date_ranges.filter((e) => (['from_comparison', 'to_comparison', 'prev'].includes(e.meta) === false))
  .map((e) => e.click_analytic_impressions);

export const getGraphDateOverTimeRange = (metric, date_ranges, current_data) => {
  let filtered_array = [];
  if (current_data === true) {
    filtered_array = date_ranges?.filter((e) => (['from_comparison', 'to_comparison', 'prev'].includes(e.meta) === false));
  } else {
    filtered_array = date_ranges?.filter((e) => (['prev'].includes(e.meta) === true));
  }
  switch (metric) {
    case 'impressions': return filtered_array.map((e) => e.click_analytic_impressions);
    case 'clicks': return filtered_array.map((e) => e.click_analytic_clicks);
    case 'ctr': return filtered_array.map((e) => e.click_analytic_ctr);
    case 'cpc': return filtered_array.map((e) => e.click_analytic_cpc);
    case 'total_payout': return filtered_array.map((e) => e.click_analytic_total_payout);
    case 'avg_position': return filtered_array.map((e) => e.click_analytic_avg_position);
    case 'mobile_traffic': return filtered_array.map((e) => (getMobileDataPercentage(e)));
    case 'avg_bid': return filtered_array.map((e) => e.click_analytic_avg_bid);
    case 'lead_analytic_total_cost': return filtered_array.map((e) => e.lead_analytic_total_cost);
    case 'lead_analytic_total_leads': return filtered_array.map((e) => e.lead_analytic_total_leads);
    case 'lead_analytic_total_opportunities': return filtered_array.map((e) => e.lead_analytic_total_opportunities);
    case 'lead_analytic_total_searches': return filtered_array.map((e) => e.lead_analytic_total_searches);
    case 'lead_analytic_total_selected': return filtered_array.map((e) => e.lead_analytic_total_selected);
    case 'lead_analytic_total_won': return filtered_array.map((e) => e.lead_analytic_total_won);
    case 'lead_analytic_avg_cpl': return filtered_array.map((e) => e.lead_analytic_avg_cpl);
    case 'call_analytic_total_cost': return filtered_array.map((e) => e.call_analytic_total_cost);
    case 'call_analytic_total_calls': return filtered_array.map((e) => e.call_analytic_total_calls);
    case 'call_analytic_avg_cpc': return filtered_array.map((e) => e.call_analytic_avg_cpc);
    case 'call_analytic_total_duration': return filtered_array.map((e) => e.call_analytic_total_duration);
    case 'call_analytic_avg_duration': return filtered_array.map((e) => e.call_analytic_avg_duration);
    case 'call_analytic_billable_calls': return filtered_array.map((e) => e.call_analytic_billable_calls);
    case 'leads_generated_count': return filtered_array.map((e) => e.leads_generated_count);
    case 'calls_generated_count': return filtered_array.map((e) => e.calls_generated_count);
    case 'total_count': return filtered_array?.map((e) => e?.total_count);
    case 'accepted': return filtered_array?.map((e) => e?.accepted);
    case 'click_conversions_count': return filtered_array.map((e) => e.click_conversions_count);
    case 'click_conversions_revenue': return filtered_array.map((e) => e.click_conversions_revenue);
  }
};

export const getLastDataPointForMetric = (metric, date_ranges) => {
  switch (metric) {
    case 'impressions': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.click_analytic_impressions;
    case 'total_payout': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.click_analytic_total_payout;
    case 'clicks': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.click_analytic_clicks;
    case 'cpc': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.click_analytic_cpc;
    case 'ctr': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.click_analytic_ctr;
    case 'avg_position': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.click_analytic_avg_position;
    case 'mobile_traffic': return getMobileTrafficData(date_ranges, true);
    case 'avg_bid': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.click_analytic_avg_bid;
    case 'lead_analytic_total_cost': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_cost;
    case 'lead_analytic_total_leads': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_leads;
    case 'lead_analytic_total_opportunities': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_opportunities;
    case 'lead_analytic_total_searches': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_searches;
    case 'lead_analytic_total_selected': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_selected;
    case 'lead_analytic_total_won': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_won;
    case 'lead_analytic_avg_cpl': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_avg_cpl;
    case 'call_analytic_total_cost': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_opportunities;
    case 'call_analytic_total_calls': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_searches;
    case 'call_analytic_avg_cpc': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_selected;
    case 'call_analytic_total_duration': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_total_won;
    case 'call_analytic_avg_duration': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_avg_cpl;
    case 'call_analytic_billable_calls': return date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true))
      ?.lead_analytic_avg_cpl;
  }
};

export const getClicksForGraph = (props) => props?.date_ranges.filter((e) => (['from_comparison', 'to_comparison', 'prev'].includes(e.meta) === false))
  .map((e) => e.click_analytic_clicks);

export const getCTRForGraph = (props) => props?.date_ranges.filter((e) => (['from_comparison', 'to_comparison', 'prev'].includes(e.meta) === false))
  .map((e) => e.click_analytic_ctr);

export const getAvgCPCForGraph = (props) => props?.date_ranges.filter((e) => (['from_comparison', 'to_comparison', 'prev'].includes(e.meta) === false))
  .map((e) => e.click_analytic_cpc);

export const getIncreaseValueForGraph = (selected_date_range, date_ranges) => {
  // if (selected_date_range?.label.includes("Last 7 Days"))
  {
    const per_change = calculateMetricDiffFromMetaTags(date_ranges, 'from_comparison', 'to_comparison');
    if (per_change < 0) {
      return [false, per_change.data];
    }

    return [true, per_change.data];
  }
  return [null, null];
};

export const getMaxFromStringArray = (str_arr) => {
  const num_array = str_arr.map((ele) => _.toInteger(ele));
  const max = (Math.max.apply(Math, num_array));
  return str_arr.length === 0 ? 20 : max;
};

export const getMetricCodeFromMetricName = (metric_name) => {
  switch (metric_name) {
    case 'Total Cost': return 'total_payout';
    case 'Clicks': return 'clicks';
    case 'Click Through Rate': return 'ctr';
    case 'Cost Per Click': return 'cpc';
    case 'Impressions': return 'impressions';
    case 'Average Position': return 'avg_position';
    case 'Mobile Traffic': return 'mobile_traffic';
    case 'Average Bid': return 'avg_bid';
    case 'Total Count': return 'total_count';
    case 'Accepted': return 'accepted'
  }
};

export const getMetricCodeFromMetricNameLeads = (metric_name) => {
  switch (metric_name) {
    case 'Bids': return 'lead_analytic_total_searches';
    case 'Leads': return 'lead_analytic_total_leads';
    case 'Opportunities': return 'lead_analytic_total_opportunities';
    case 'Posts': return 'lead_analytic_total_won';
    case 'Avg Cost per Lead': return 'lead_analytic_avg_cpl';
    case 'Total Cost': return 'lead_analytic_total_cost';
  }
};

export const getMetricCodeFromMetricNameCalls = (metric_name) => {
  switch (metric_name) {
    case 'Total Talk Time': return 'call_analytic_total_duration';
    case 'Total Calls': return 'call_analytic_total_calls';
    case 'Opportunities': return 'lead_analytic_total_opportunities';
    case 'Avg Call Time': return 'call_analytic_avg_duration';
    case 'Avg Cost Per Call': return 'call_analytic_avg_cpc';
    case 'Total Cost': return 'call_analytic_total_cost';
    case 'Billable Calls': return 'call_analytic_billable_calls';
  }
};

export const getMetricCodeFromMetricNameQuoteFunnels = (metric_name) => {
  switch (metric_name) {
    case 'Total Cost': return 'total_payout';
    case 'Clicks': return 'clicks';
    case 'Click Through Rate': return 'ctr';
    case 'Cost Per Click': return 'cpc';
    case 'Impressions': return 'impressions';
    case 'Leads': return 'leads_generated_count';
    case 'Calls': return 'calls_generated_count';
    case 'Conversion': return 'click_conversions_count';
  }
};

export const getComparisonDateRangeOptions = (timezone, selectedDateRange, alignToWeekday = false, today = new Date()) => {
  const showSameDayLastWeek = alignToWeekday && selectedDateRange?.original_label === DATE_RANGE_OPTIONS.TODAY;
  const options = showSameDayLastWeek
    ? [COMPARE_DATE_RANGE_OPTIONS.SAME_DAY_LAST_WEEK, ...Object.values(DATE_RANGE_OPTIONS)]
    : Object.values(DATE_RANGE_OPTIONS);
  return options.map(option => createOption(option, today, timezone));
};

const getCalendarDateInTimezone = (date, timezone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const shiftCalendarDate = (date, days) => {
  const [year, month, day] = date.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}`;
};

const calendarDayDistance = (startDate, endDate) => {
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
  return (Date.UTC(endYear, endMonth - 1, endDay) - Date.UTC(startYear, startMonth - 1, startDay)) / 86400000;
};

const zonedDayBoundary = (date, timezone, time) => zonedTimeToUtc(`${date}T${time}`, timezone);

export const getPrevDataRangeFromSelectedDataRange = (selectedDateRange, timezone, new_compare_to_logic, alignToWeekday = false) => {
  let start_date = new Date(selectedDateRange?.value?.startDate);
  let end_date = new Date(selectedDateRange?.value?.endDate);
  const diff_days = differenceInDays(end_date, start_date) + 1;

  if (alignToWeekday && selectedDateRange.original_label === DATE_RANGE_OPTIONS.CUSTOM_RANGE) {
    // Shift by whole calendar weeks in the reporting timezone. This keeps weekdays
    // aligned and avoids overlap for ranges longer than one week, including DST.
    // Do date arithmetic on UTC calendar dates so the browser's timezone cannot
    // alter the selected reporting timezone's calendar days.
    const localStart = getCalendarDateInTimezone(start_date, timezone);
    const localEnd = getCalendarDateInTimezone(end_date, timezone);
    const rangeDays = calendarDayDistance(localStart, localEnd) + 1;
    const shiftDays = Math.ceil(rangeDays / 7) * 7;
    start_date = zonedDayBoundary(shiftCalendarDate(localStart, -shiftDays), timezone, '00:00:00.000');
    end_date = zonedDayBoundary(shiftCalendarDate(localEnd, -shiftDays), timezone, '23:59:59.999');
  } else if (DATE_RANGE_OPTIONS.THIS_QUARTER == selectedDateRange.original_label && new_compare_to_logic) {
    [start_date, end_date] = getStartAndEndDateForPrevQuarter(start_date, end_date, timezone, diff_days)
  } else if (DATE_RANGE_OPTIONS.THIS_YEAR == selectedDateRange.original_label && new_compare_to_logic) {
    [start_date, end_date] = getStartAndEndDateForPrevYear(start_date, end_date, timezone, diff_days)
  } else if (DATE_RANGE_OPTIONS.LAST_QUARTER == selectedDateRange.original_label && new_compare_to_logic) {
    [start_date, end_date] = getPrevStartAndEndDateForLastQuarter(start_date, timezone);
  } else {
    start_date = getZonedStartOfDay(addDays(start_date, -diff_days), timezone);
    end_date = getZonedEndOfDay(addDays(end_date, -diff_days), timezone);
  }

  let prev_date_range_options = [];
  switch (selectedDateRange.original_label) {
    case 'THIS_MONTH':
      prev_date_range_options.push(createOption(
        DATE_RANGE_OPTIONS.LAST_MONTH,
        new Date(),
        timezone,
      ));
      break;
    case 'TODAY':
      prev_date_range_options.push(createOption(
        alignToWeekday ? COMPARE_DATE_RANGE_OPTIONS.SAME_DAY_LAST_WEEK : DATE_RANGE_OPTIONS.YESTERDAY,
        new Date(),
        timezone,
      ));
      break;
    case 'THIS_WEEK':
      prev_date_range_options.push(createOption(
        DATE_RANGE_OPTIONS.LAST_WEEK,
        new Date(),
        timezone,
      ));
      break;

    case 'ALL_TIME': break;
    default:
      prev_date_range_options.push(
        {
          label: createLabelWithDateRange(
            {
              label: '',
              startDate: start_date,
              endDate: end_date,
            },
            timezone,
          ),
          original_label: 'CUSTOM_COMPARE',
          value: {
            startDate: start_date,
            endDate: end_date,
          },
        },
      );
  }
  prev_date_range_options = prev_date_range_options.concat(
    getComparisonDateRangeOptions(timezone, selectedDateRange, alignToWeekday)
      .filter(option => option.original_label !== prev_date_range_options[0]?.original_label)
  );
  return prev_date_range_options;
};

export const getMobileTrafficData = (date_ranges, prev) => {
  let data = date_ranges.find((e) => (['from_comparison'].includes(e.meta) === true));
  if (!prev) {
    data = date_ranges.find((e) => (['to_comparison'].includes(e.meta) === true));
  }
  return getMobileDataPercentage(data);
};

export const timeFormatter = (seconds) => {
  if (!seconds) {
    seconds = 0
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export const getMobileDataPercentage = (data) => {
  if (!data?.click_analytic_searches) {
    return data?.click_analytic_searches;
  }
  const per = (data?.click_analytic_mobile_traffic
    / (data?.click_analytic_searches)) * 100.0;
  return per.toFixed(2);
};

const TIMEFRAME_TYPES = {
  hour: 'hour',
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year'
}

// XXX: this works currently because reporting timezone is the same as account timezone and all
// reporting is done on account timezone
export const parseDashboardAPIPayload = (payload, api_type, ianaTz = REPORTING_TIMEZONE) => {
  switch (api_type) {
    case DASHBOARD_API_CALL_TYPE.FETCH_CALLS_MAIN_DATA:
      return [
        {
          call_analytic_opportunities: 0,
          call_analytic_searches: 0,
          call_analytic_total_calls: 0,
          call_analytic_distinct_calls: 0,
          call_analytic_billable_calls: 0,
          call_analytic_total_cost: "0.00",
          call_analytic_total_call_duration: 0,
          call_analytic_avg_call_duration: 0,
          call_analytic_avg_cpc: "0.00",
          call_analytic_avg_duration: payload[0]?.call_analytic_avg_call_duration || 0,
          call_analytic_total_duration: payload[0]?.call_analytic_total_call_duration || 0,
          ...payload[0]
        }
      ]
    case DASHBOARD_API_CALL_TYPE.FETCH_LEADS_MAIN_DATA:
    case DASHBOARD_API_CALL_TYPE.FETCH_LEADS_COMPARE_TO_MAIN_DATA:
      //Calculate avg cpl
      const avg_cpl = parseFloat(payload[0]?.lead_analytic_total_cost) / payload[0]?.lead_analytic_total_leads || 0;
      return [
        {
          ...payload[0],
          lead_analytic_avg_cpl: avg_cpl
        }
      ]
    case DASHBOARD_API_CALL_TYPE.FETCH_LEADS_CAMPAIGN_WISE_METRICS:
    case DASHBOARD_API_CALL_TYPE.FETCH_LEADS_STATE_WISE_METRICS:
    case DASHBOARD_API_CALL_TYPE.FETCH_LEADS_INSURANCE_TYPE_WISE_METRICS:
      //Calculate avg cpl
      const agg_data = payload.map(e => {
        const avg_cpl = parseFloat(e.lead_analytic_total_cost) / e.lead_analytic_total_leads || 0;
        return {
          ...e,
          lead_analytic_avg_cpl: avg_cpl
        }
      })
      return agg_data;
    case DASHBOARD_API_CALL_TYPE.FETCH_DATE_RANGES:
    case DASHBOARD_API_CALL_TYPE.FETCH_LEADS_DATE_RANGES:
    case DASHBOARD_API_CALL_TYPE.FETCH_CALLS_DATE_RANGES:

      {
        const ret = payload.map(period_data => {
          // if meta is already present, just return the period_data (in case of prev)
          if (period_data.meta) {
            if (api_type !== DASHBOARD_API_CALL_TYPE.FETCH_DATE_RANGES) {
              //Calculate avg cpl for calls and leads type only
              const avg_cpl = parseFloat(period_data.lead_analytic_total_cost) / period_data.lead_analytic_total_leads || 0;

              return {
                ...period_data,
                lead_analytic_avg_cpl: avg_cpl,
              }
            }
            return period_data;
          }

          let meta = '';
          const zonedStart = utcToZonedTime(period_data.timeframe_start, ianaTz);
          switch (period_data.timeframe_type) {
            case TIMEFRAME_TYPES.hour:
              meta = `${format(zonedStart, 'hh:mm a')} - ${format(addMinutes(zonedStart, 59), 'hh:mm a')}`
              break;
            case TIMEFRAME_TYPES.day:
              meta = `${format(zonedStart, 'LLL dd')}`;
              break;
            case TIMEFRAME_TYPES.week:
              meta = `${format(zonedStart, 'LLL dd')} - ${format(addWeeks(zonedStart, 1), 'LLL dd')}`
              break;
            case TIMEFRAME_TYPES.month:
              meta = `${format(zonedStart, 'MMM yy')}`;
              break;
            case TIMEFRAME_TYPES.year:
              meta = `${format(zonedStart, 'yyyy')}`;
              break;
          }
          if (api_type !== DASHBOARD_API_CALL_TYPE.FETCH_DATE_RANGES) {
            //Calculate avg cpl for calls and leads type only
            const avg_cpl = parseFloat(period_data.lead_analytic_total_cost) / period_data.lead_analytic_total_leads || 0;

            return {
              ...period_data,
              lead_analytic_avg_cpl: avg_cpl,
              meta
            }
          }
          return {
            ...period_data,
            meta
          }
        })
        return ret;
      }
    default:
      return payload
  }
}

// for UI purpose, an offset is added at the start of the x -axis for miniWidget

export const xAxisOffsetForHourTimeFrame = (options, props) => {
  if (props?.date_ranges?.[0]?.timeframe_type === TIMEFRAME_TYPES.hour) {
    return {
      ...options,
      xaxis: {
        ...options.xaxis,
        min: 0,
        tickAmount: props?.date_ranges?.length,
      }
    }
  } else {
    return options
  }
}

// change previous date range period to corresponding selected date range period
export const changePrevTimeRangeBySelectedTimeRange = (prevTimeRange, selectedTimeRange) => {
  let selectedTimeRangeLabel = selectedTimeRange.original_label
  let prevSelectedTimeRangeLabel = prevTimeRange.original_label
  if (selectedTimeRangeLabel == DATE_RANGE_OPTIONS.ALL_TIME) {
    return
  }
  const duration = new Date(selectedTimeRange?.value?.endDate).getTime() - new Date(selectedTimeRange?.value?.startDate).getTime()
  const prevEndDate = new Date(prevTimeRange?.value?.startDate).getTime() + duration;
  // if (selectedTimeRangeLabel === DATE_RANGE_OPTIONS.CUSTOM_RANGE) {
  //   let selectedStartDate = selectedTimeRange.value.startDate
  // }
  switch (selectedTimeRangeLabel) {
    case DATE_RANGE_OPTIONS.TODAY:
    case DATE_RANGE_OPTIONS.THIS_WEEK:
    case DATE_RANGE_OPTIONS.THIS_MONTH:
    case DATE_RANGE_OPTIONS.THIS_YEAR:
    case DATE_RANGE_OPTIONS.THIS_QUARTER:
      if ([COMPARE_DATE_RANGE_OPTIONS.SAME_DAY_LAST_WEEK, DATE_RANGE_OPTIONS.YESTERDAY, DATE_RANGE_OPTIONS.LAST_WEEK,
      DATE_RANGE_OPTIONS.LAST_MONTH].includes(prevSelectedTimeRangeLabel)) {
        return new Date(prevEndDate)
      } else {
        return prevTimeRange.value.endDate
      }
    case DATE_RANGE_OPTIONS.ALL_TIME: break;
    default:
      return prevTimeRange.value.endDate
  }
}

export const getCurrentTime = (selectedDateRange) => {
  if ([DATE_RANGE_OPTIONS.TODAY, DATE_RANGE_OPTIONS.THIS_WEEK,
  DATE_RANGE_OPTIONS.THIS_MONTH, DATE_RANGE_OPTIONS.THIS_QUARTER,
  DATE_RANGE_OPTIONS.THIS_YEAR].includes(selectedDateRange.original_label)) {
    return getZonedEndOfHour(new Date(), REPORTING_TIMEZONE);
  }
  return selectedDateRange.value.endDate
}

export const getStartAndEndDateForPrevQuarter = (start_date, _end_date, timezone, diff_days) => {
  const startOfThisQuarter = getZonedStartOfQuarter(start_date, timezone);
  const prev_start_date = getStartOfDayHandleDST(subQuarters(startOfThisQuarter, 1), timezone);
  const prev_end_date = getZonedEndOfDay(addDays(prev_start_date, diff_days - 1), timezone)
  return [prev_start_date, prev_end_date]
}

export const getStartAndEndDateForPrevYear = (start_date, end_date, timezone, diff_days) => {
  let year = start_date.getFullYear()
  let temp_start_date = new Date(start_date)
  temp_start_date.setFullYear(year - 1, 0, 1)
  start_date = getZonedStartOfYear(temp_start_date, timezone)
  end_date = getZonedEndOfDay(addDays(start_date, diff_days - 1), timezone)
  return [start_date, end_date]
}

export const getPrevStartAndEndDateForLastQuarter = (start_date, timezone) => {
  // adding one day will make sure that the conversion from dst quarter start date to non-dst quarter start date is handled
  const previousQuarterDate = addDays(subQuarters(start_date, 1), 1);
  const prev_start_date = getZonedStartOfQuarter(previousQuarterDate, timezone);
  const prev_end_date = getZonedEndOfQuarter(previousQuarterDate, timezone);
  return [prev_start_date, prev_end_date]
}

export const calculateMissingTotals = (tableData, main_data) => {
  if (!tableData || tableData.length === 0) {
    return main_data;
  }

  // Calculate totals from individual rows
  const totals = tableData.reduce((acc, row) => {
    acc.click_analytic_total_upstream_bid = (acc.click_analytic_total_upstream_bid || 0) + (parseFloat(row.click_analytic_total_upstream_bid) || 0);
    acc.click_analytic_profit = (acc.click_analytic_profit || 0) + (parseFloat(row.click_analytic_profit) || 0);
    return acc;
  }, {});

  // Calculate derived values
  const totalClicks = parseFloat(main_data?.click_analytic_clicks) || 0;
  const totalSpend = totals.click_analytic_total_upstream_bid || 0;
  const totalProfit = totals.click_analytic_profit || 0;

  // Calculate derived metrics
  if (totalClicks > 0) {
    totals.click_analytic_rpc = totalSpend / totalClicks;
    totals.click_analytic_gp_per = totalProfit / totalClicks;
  }

  if (totalSpend > 0) {
    totals.click_analytic_cm = (totalProfit / totalSpend) * 100;
  }

  return {
    ...main_data,
    ...totals
  };
}

export const addTotalsToColumns = (cols, main_data, stat_column_names, tableData = null) => {
  // Calculate missing totals if tableData is provided
  const enhancedMainData = tableData ? calculateMissingTotals(tableData, main_data) : main_data;
  
  return cols.map(elem => {
    const customFooterFormatter = (column) => {
      const is_stat_column = stat_column_names.includes(column?.dataField);
      const data = column?.footerData?.[column?.dataField];
      if (elem?.formatter && is_stat_column) {
        return elem?.formatter(data, column?.footerData || {}, 0);
      }
      return data;
    }
    return ({
      ...elem,
      footerData: enhancedMainData,
      footer: '',
      footerFormatter: elem?.footerFormatter || customFooterFormatter
    })
  })
}

export const isDataDiffPresent = (dataDiff) => {
  return (dataDiff || '').replace('%', '') !== '';
}
/**
 * @brief Function to add/remove motorcycle lead type from lead type options
 * @param {*} lead_type_options
 * @param {*} motorcycle_feature_flag 
 * @param {*} product_type_name 
 * @returns 
 */
export const filterMotorcycleFromLeadTypesOptions = (lead_type_options, motorcycle_feature_flag, product_type_name) => {
  if (motorcycle_feature_flag && product_type_name == NAME_OF_PRODUCT_TYPES.CLICKS) {
    lead_type_options.push({ value: LEAD_TYPE_IDS.MOTORCYCLE, label: capitalizeWord(NAME_OF_LEAD_TYPES.MOTORCYCLE) })
    return _.uniqBy(lead_type_options, 'value')
  } else {
    return lead_type_options?.filter((el) => el?.value !== LEAD_TYPE_IDS.MOTORCYCLE)
  }
}
/**
 * @brief Function to get campaign options for each brand
 * @param {*} campaigns
 * @param {Boolean} addAllCampaignOption,  - Decides whether we need to append All Campaigns or not in the campaign options.
 * @param {Boolean} useDefaultBrandName - When true uses brand_name else brand.name from the campaign
 [
     {
         "id": 554,
         ...
         "brand": {
             "id": 96,
             "name": "RTB UI Changes",
             "account_id": 62,
             "discarded_at": null,
             "active": true
         },
         "lead_integrations": []
     },
     {
         "id": 555,
         ...
         "brand": {
             "id": 97,
             "name": "New brand 1",
             "account_id": 62,
             "discarded_at": null,
             "active": true
         },
     }
 ]
 * @returns Options that are in the format for parent child multiselect checkbox
[
    {
        "label": "All Campaigns",
        "value": "all"
    },
    {
        "label": "RTB UI Changes",
        "options": [
            {
                "value": 554,
                "label": "Clicks Auto"
            }
        ]
    },
    {
        "label": "New brand 1",
        "options": [
            {
                "value": 556,
                "label": "clicks home new brand 1"
            },
            {
                "value": 555,
                "label": "New Brands 1 Clicks Auto"
            }
        ]
    }
]
 */
export const createBrandCampaignOptions = (campaigns, addAllCampaignOption = true, useDefaultBrandName = false) => {
  const brandCampaignMap = campaigns.reduce((acc, campaign) => {
    const brandName = useDefaultBrandName ? campaign?.brand_name : campaign?.brand?.name

    if (!acc[brandName]) {
      acc[brandName] = []
    }

    acc[brandName].push({
      value: campaign?.id,
      label: campaign?.name,
    });

    return acc
  }, {})
  //Till this point we will have foloowing value for brandCampaignMap
  //   {
  //     "RTB UI Changes": [
  //         {
  //             "value": 554,
  //             "label": "Clicks Auto"
  //         }
  //     ],
  //     "New brand 1": [
  //         {
  //             "value": 555,
  //             "label": "New Brands 1 Clicks Auto"
  //         }
  //     ]
  // }
  // Convert the map into the format required by react-multiselect-checkboxes
  const options = [
    ...Object.keys(brandCampaignMap).map(brandName => ({
      label: brandName,
      options: brandCampaignMap[brandName]
    }))
  ];
  if (addAllCampaignOption) {
    options.unshift({ label: 'All Campaigns', value: 'all' })
  }
  return options;
}

/**
 * Returns campaign options in the format required by react-select
 * @param {Array} campaigns - All campaigns in the account.
 * @param {Boolean} showRtbBrandUiChanges - Flag that determines whether to show new UI for campaigns single select.
 * @param {Boolean} useDefaultBrandName - When true uses brand_name else brand.name from the campaign
 * 
 */
export const getCampaignOptions = (campaigns, showRtbBrandUiChanges, useDefaultBrandName = true) => {
  if (showRtbBrandUiChanges) {
    return createBrandCampaignOptions(campaigns, false, useDefaultBrandName);
  }
  return campaigns?.map(campaign => ({ label: campaign.name, value: campaign.id }));
}

